<template>
    <div>
        <v-dialog
            :value="isOpen"
            max-width="1100px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card>
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        Lista pakietów
                    </h1>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <v-data-table
                                :headers="headers"
                                :items="goods"
                                dense
                                item-key="id"
                                :mobile-breakpoint="0"
                                hide-default-footer
                                disable-pagination
                                name="productsData"
                                no-data-text="Brak danych"
                            >
                                <template #item.name="{item}">
                                    <span>
                                        {{ item.goodName }}
                                    </span>
                                </template>
                                <template #item.code="{item}">
                                    <span>
                                        {{ parseEmptyValue(item.goodCode) }}
                                    </span>
                                </template>
                                <template #item.index="{item}">
                                    <span>
                                        {{ parseEmptyValue(item.index) }}
                                    </span>
                                </template>
                                <template #item.amount="{item}">
                                    <v-edit-dialog
                                        :return-value.sync="item.chosenAmount"
                                        large
                                        lazy
                                        cancel-text="Anuluj"
                                        save-text="Zapisz"
                                    >
                                        <div class="d-inline-flex" :class="{'error--text': isError}">
                                            {{ item.chosenAmount }} / {{ item.amount }}
                                            <v-icon color="primary" small class="ml-1">
                                                mdi-pencil
                                            </v-icon>
                                        </div>
                                        <template #input>
                                            <v-text-field
                                                v-model.number="item.chosenAmount"
                                                v-mask="'#######'"
                                                label="Ilość"
                                                required
                                                single-line
                                                @input="setAllowedAmount($event, item)"
                                            />
                                        </template>
                                    </v-edit-dialog>
                                </template>
                                <template #item.actions>
                                    <div class="d-inline-flex">
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Edytuj"
                                                    v-on="on"
                                                >
                                                    <v-icon color="teal">
                                                        mdi-pencil
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Edytuj</span>
                                        </v-tooltip>
                                    </div>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-btn
                        color="error"
                        text
                        data-sentry-label="Zamknij"
                        @click="onClose"
                    >
                        Anuluj
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="success"
                        text
                        data-sentry-label="Zapisz"
                        @click="onSave"
                    >
                        <template>
                            Zapisz
                        </template>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse'

export default {
    name: 'ShippingLabelCampaignPositionGoodsDialog',
    mixins: [
        tooltipDelay,
        parse
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        positionGoods: {
            type: Array,
            default: () => []
        },
        packageIndex: {
            type: Number,
            default: null
        },
        positionIndex: {
            type: Number,
            default: null
        },
    },
    data() {
        return {
            positionGoodsDialog: false,
            goods: [{
                chosenAmount: 0
            }],
            headers: [
                {
                    text: 'Nazwa',
                    value: 'name',
                    align: 'left',
                    sortable: false,
                    width: 300,
                    showOnRead: true
                },
                {
                    text: 'Kod EAN',
                    value: 'code',
                    align: 'center',
                    sortable: false,
                    width: 120,
                    showOnRead: true
                },
                {
                    text: 'Indeks',
                    value: 'index',
                    align: 'center',
                    sortable: false,
                    width: 100,
                    showOnRead: true
                },
                {
                    text: 'Ilość',
                    value: 'amount',
                    align: 'right',
                    sortable: false,
                    width: 50,
                    showOnRead: true
                },
            ],
            error: false
        }
    },
    computed: {
        isError() {
            return this.error
        },
        totalChosenAmount() {
            return this.goods.reduce((sum, item) => sum + (item.chosenAmount || 0), 0)
        },
        totalAvailableAmount() {
            return this.goods.reduce((sum, item) => sum + item.amount, 0)
        },
        isValidTotalAmount() {
            return this.totalChosenAmount + 1 <= this.totalAvailableAmount
        }
    },
    watch: {
        isOpen(value) {
            if (value) {
                this.goods = JSON.parse(JSON.stringify(this.positionGoods))
                this.goods = this.goods.map(good => ({
                    ...good,
                    chosenAmount: 0
                }))
            }
        },
        goods: {
            deep: true,
            handler() {
                this.error = !this.isValidTotalAmount
            }
        }
    },
    methods: {
        setAllowedAmount(value, item) {
            if (value > item.amount) {
                this.$set(item, 'chosenAmount', item.amount)
            } else if (value < 0) {
                this.$set(item, 'chosenAmount', 0)
            }
        },
        closePositionGoodsDialog() {
            this.positionGoodsDialog = false
        },
        availableAmount(product) {
            return product.sourceGoodUnitPackageQuantity - product.assignedAmount
        },
        onSave() {
            if (!this.isValidTotalAmount) {
                this.error = true
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Suma wybranych ilości musi być mniejsza od sumy dostępnych ilości o co najmniej 1'
                })
                
                return
            }

            const selectedGoods = this.goods.filter(good => good.chosenAmount > 0)
            const newPackageGoods = selectedGoods.map(good => ({
                amount: good.chosenAmount,
                goodCode: good.goodCode,
                goodId: good.goodId,
                good: good.good,
                goodName: good.goodName,
                index: good.index,
                unitPackageTypeId: null,
                hasChanges: true,
            }))

            // Update original goods by subtracting chosen amounts
            const updatedOriginalGoods = this.positionGoods.map(good => {
                const selectedGood = selectedGoods.find(selected => selected.goodId === good.goodId)
                
                if (selectedGood) {
                    return {
                        ...good,
                        amount: good.amount - selectedGood.chosenAmount,
                        hasChanges: true,
                    }
                }

                return good
            }).filter(good => good.amount > 0)

            this.$emit('on-save', newPackageGoods, updatedOriginalGoods)
            this.onClose()
        },
        onClose() {
            this.$emit('on-close')
            setTimeout(() => {
                this.closePositionGoodsDialog()
            }, 500)
        },
        
    }
}
</script>